import React, { useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import MenuItem from "@components/molecules/MenuItem";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { MenuItemProps, MoreScreenProps } from "./types";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { View } from "@gluestack-ui/themed";
import usePostOnboarding from "../../../hooks/usePostOnboardingData";
import { CoachOverlayStep } from "@components/CoachOverlay";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { getBoxShadow } from "@utils/product/productValidators";
import { Platform } from "react-native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { css } from "styled-components/native";

interface SectionContainerProps extends MoreScreenProps {
  selectedItemId: string | null;
  onItemSelect: (item: MenuItemProps) => void;
  menuContainerProps?: (typeof MenuItemContainer)["defaultProps"];
  direction?: string;
  showAsPopover?: boolean;
  isFocused?: boolean;
  coachScreenConfig?: any;
  callOverlay?: () => void;
  renderCustomItem?: (
    item: MenuItemProps,
    index: number,
    renderDefaultItem: (item: MenuItemProps, index: number) => JSX.Element
  ) => JSX.Element;
}

const SectionContainer: React.FC<SectionContainerProps> = ({
  title,
  hintText,
  data,
  selectedItemId,
  onItemSelect,
  showAsPopover = false,
  coachScreenConfig,
  callOverlay,
  renderCustomItem,
  menuContainerProps,
  direction,
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();

  // Use the screenConfig passed from parent (MoreScreen)
  const screenConfig = coachScreenConfig;

  const userInfo = useUserInfo();
  const showCurrency =
    userInfo.currencyConversion() && !userInfo.lockCurrency();
  const { t } = useTranslation();
  const [allowPostOnboadingNavigation, setAllowPostOnboadingNavigation] =
    useState(false);
  const onNavigationSucceeded = (isSucceeded: boolean) => {
    if (isSucceeded) setAllowPostOnboadingNavigation(false);
  };

  usePostOnboarding(
    !showAsPopover && allowPostOnboadingNavigation,
    onNavigationSucceeded,
    showAsPopover ? null : undefined
  );

  const handleItemPress = (item: MenuItemProps) => {
    onItemSelect(item);
  };

  const renderDefaultItem = (item: MenuItemProps, index: number) => {
    const stepConfig =
      screenConfig?.steps?.find((step: any) => step?.key === item?.id) || null;

    return (
      <React.Fragment key={`${item.value || item.id}-${index}`}>
        {stepConfig && !showAsPopover ? (
          <CoachOverlayStep
            screen="MoreScreen"
            stepKey={stepConfig?.key}
            text={stepConfig?.text || ""}
            order={screenConfig?.steps?.indexOf(stepConfig)}
          >
            <MenuItem
              {...item}
              isSelected={selectedItemId === item.id}
              onPress={() => handleItemPress(item)}
              arrLength={data?.length}
              arrIndex={index}
            />
          </CoachOverlayStep>
        ) : (
          <MenuItem
            {...item}
            isSelected={selectedItemId === item.id}
            onPress={() => handleItemPress(item)}
            arrLength={data?.length}
            arrIndex={index}
          />
        )}
        {index !== data.length - 1 && (
          <LineViewContainer background="core-02" marginLeft marginRight />
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      {!showAsPopover && (
        <>
          <SectionTitle>
            <Spacer size={SpaceType.Basic} />
            {title && (
              <VDBText color={theme["core-06"]} type={typography["H3-M"]}>
                {t(title)}
              </VDBText>
            )}
          </SectionTitle>
          <Spacer size={SpaceType.Basic} />
        </>
      )}
      <MenuItemContainer
        key={title}
        {...menuContainerProps}
        {...(Platform.OS !== "web" &&
          callOverlay && { onLayout: () => callOverlay() })}
        {...(showAsPopover && {
          direction: direction,
          ...(showAsPopover &&
            direction === "row" && { justifyContent: "space-between" }),
        })}
      >
        {data.map((item: MenuItemProps, index: number) =>
          renderCustomItem instanceof Function ? (
            <View
              {...(showAsPopover && {
                style: {
                  ...(item.id === "languages" && !showCurrency
                    ? { width: "100%" }
                    : {
                        width: item?.width,
                        justifyContent: item?.width
                          ? "space-between"
                          : "space-evenly",
                      }),
                },
              })}
            >
              {renderCustomItem(item, index, renderDefaultItem)}
            </View>
          ) : (
            renderDefaultItem(item, index)
          )
        )}
      </MenuItemContainer>
      {hintText && showAsPopover && (
        <>
          <Spacer size={SpaceType.Basic} />
          <HintMessageContainer>
            <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
              {"* "}
              {t(hintText)}
            </VDBText>
          </HintMessageContainer>
        </>
      )}
      <Spacer size={showAsPopover ? SpaceType.Big : SpaceType.ExtraLarge} />
    </>
  );
};

const SectionTitle = styled.View`
  flex-direction: row;
  align-items: center;
`;

const MenuItemContainer = styled(View)`
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor ?? theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  ${({ backgroundColor }) =>
    backgroundColor !== "transparent" &&
    css`
      box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
    `}
  flex-direction: ${({ direction }) => direction ?? "column"};
`;

const HintMessageContainer = styled.View`
  flex-direction: row;
`;

export default SectionContainer;
